<template>
  <AuthLayout
    :sidebar-title="sidebarTitle"
    :sidebar-text="sidebarText"
    :support-phone="supportPhone"
    :is-admin="isAdmin"
    :title="title"
    :subtitle="subtitle"
  >
    <div
      role="radiogroup"
      aria-label="Choose how to receive your reset link"
      @keydown="onGroupKeydown"
    >
      <button
        type="button"
        role="radio"
        class="auth-login__option"
        :class="{ 'auth-login__option--active': selectedChannel === MFA_METHOD.EMAIL }"
        :aria-checked="selectedChannel === MFA_METHOD.EMAIL ? 'true' : 'false'"
        :disabled="loading || isCoolingDown"
        :tabindex="selectedChannel === MFA_METHOD.EMAIL ? 0 : -1"
        @click="activateChannel(MFA_METHOD.EMAIL)"
      >
        <span class="auth-login__option-title">
          <AuthIcon name="mail" class="auth-login__option-icon" />
          Email
        </span>
      </button>

      <button
          type="button"
          role="radio"
          class="auth-login__option"
          :class="{ 'auth-login__option--active': selectedChannel === MFA_METHOD.SMS }"
          :aria-checked="selectedChannel === MFA_METHOD.SMS ? 'true' : 'false'"
          :disabled="loading || isCoolingDown"
          :tabindex="selectedChannel === MFA_METHOD.SMS || !selectedChannel ? 0 : -1"
          @click="activateChannel(MFA_METHOD.SMS)"
      >
        <span class="auth-login__option-title">
          <AuthIcon name="smartphone" class="auth-login__option-icon" />
          Text Message (SMS)
        </span>
      </button>
    </div>

    <span v-if="error" class="auth-login__error-block" role="alert">{{ error }}</span>

    <p v-if="isCoolingDown" class="auth-login__cooldown-hint">
      Please wait {{ countdown }} before requesting another link.
    </p>

    <button type="button" class="auth-login__back" :disabled="loading" @click="emit('cancel')">
      ← Back to sign in
    </button>
  </AuthLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { MFA_METHOD } from '../constants/steps.js'
import { isOptionNavigationKey, moveOptionSelection } from '../utils/optionRadiogroup.js'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthIcon from '../../shared/components/AuthIcon.vue'

const props = defineProps({
  loading:       { type: Boolean, default: false },
  error:         { type: String,  default: '' },
  isCoolingDown: { type: Boolean, default: false },
  countdown:     { type: String,  default: '1:00' },
  supportPhone:  { type: String,  default: '815-908-2020' },
  isAdmin:       { type: Boolean, default: false },
  passwordResetRequired: { type: Boolean, default: false },
})

const emit = defineEmits(['send-link', 'cancel'])

const channelOptions = [MFA_METHOD.SMS, MFA_METHOD.EMAIL]
const selectedChannel = ref(null)

const sidebarTitle = computed(() => props.passwordResetRequired
  ? 'Secure your account'
  : 'Reset your password')
const sidebarText = computed(() => props.passwordResetRequired
  ? 'This password has appeared in a data breach. You must replace it before continuing.'
  : "We'll send you a secure link to create a new password. The link expires after a limited time.")
const title = computed(() => props.passwordResetRequired ? 'Password reset required' : 'Reset password')
const subtitle = computed(() => props.passwordResetRequired
  ? 'Choose where you would like to receive your secure reset link.'
  : 'Choose how you would like to receive your reset link.')

const channelName = (method) => (method === MFA_METHOD.SMS ? 'sms' : 'email')

const activateChannel = (method) => {
  if (props.loading || props.isCoolingDown) return
  selectedChannel.value = method
  emit('send-link', channelName(method))
}

const onGroupKeydown = (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    if (selectedChannel.value != null) {
      event.preventDefault()
      activateChannel(selectedChannel.value)
    }
    return
  }

  if (!isOptionNavigationKey(event.key)) return

  event.preventDefault()
  selectedChannel.value = moveOptionSelection(
    channelOptions,
    selectedChannel.value,
    event.key,
  )
}
</script>
